import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Trash2, Loader2, AlertTriangle, GraduationCap, ChefHat, ClipboardCheck, Award, ListChecks, UserCog, FileText } from 'lucide-react';

const DeletionManagementPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const [tableStats, setTableStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const tables = [
        { key: 'all_students', label: 'Students', icon: GraduationCap, statKey: 'students', color: 'bg-blue-500', desc: 'All student records and associated attendance & grades' },
        { key: 'all_lessons', label: 'Lessons', icon: ChefHat, statKey: 'lessons', color: 'bg-emerald-500', desc: 'All lesson records and associated attendance & grades' },
        { key: 'all_assessments', label: 'Assessments', icon: FileText, statKey: 'assessments', color: 'bg-violet-500', desc: 'All assessment records and associated attendance & grades' },
        { key: 'all_attendance', label: 'Attendance', icon: ClipboardCheck, statKey: 'attendance', color: 'bg-amber-500', desc: 'All attendance records across all lessons and assessments' },
        { key: 'all_grades', label: 'Grades', icon: Award, statKey: 'grades', color: 'bg-purple-500', desc: 'All grade records across all lessons and assessments' },
        { key: 'all_criteria', label: 'Criteria', icon: ListChecks, statKey: 'criteria', color: 'bg-rose-500', desc: 'All grading criteria definitions' },
        { key: 'all_users', label: 'Users', icon: UserCog, statKey: 'users', color: 'bg-cyan-500', desc: 'All user accounts except the primary admin' },
    ];

    useEffect(() => { fetchStats(); }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/table-stats`, { headers: getAuthHeaders() });
            setTableStats(res.data);
        } catch (e) { toast.error('Failed to load stats'); }
        finally { setLoading(false); }
    };

    const openConfirm = (table) => {
        setSelectedTable(table);
        setConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedTable) return;
        setDeleting(true);
        try {
            const res = await axios.post(`${API}/admin/bulk-delete/${selectedTable.key}`, {}, { headers: getAuthHeaders() });
            const deleted = res.data.deleted || {};
            const total = Object.values(deleted).reduce((s, v) => s + v, 0);
            toast.success(`Deleted ${total} ${selectedTable.label.toLowerCase()} record${total !== 1 ? 's' : ''}`);
            setConfirmOpen(false);
            setSelectedTable(null);
            fetchStats();
        } catch (e) { toast.error('Deletion failed'); }
        finally { setDeleting(false); }
    };

    if (!isAdmin()) {
        return (
            <Layout>
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <Trash2 className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                    <h3 className="text-base font-semibold text-gray-700 mb-1">Admin Access Required</h3>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="deletion-page">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-ica-navy">Data Deletion</h1>
                    <p className="text-gray-500 mt-1">Permanently delete records by category</p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy" /></div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {tables.map(table => {
                            const count = tableStats[table.statKey] || 0;
                            return (
                                <div key={table.key} className="bg-white rounded-lg border border-gray-200 p-5 flex flex-col" data-testid={`table-card-${table.key}`}>
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className={`w-10 h-10 ${table.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                            <table.icon className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-gray-900">{table.label}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">{table.desc}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
                                        <span className="text-2xl font-bold text-gray-900">{count}</span>
                                        <Button variant="outline" size="sm" disabled={count === 0}
                                            onClick={() => openConfirm(table)}
                                            className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                                            data-testid={`delete-${table.key}`}>
                                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Single Confirmation Dialog */}
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="delete-confirm-modal">
                    <DialogTopBar title={<><AlertTriangle className="w-5 h-5" /> Delete All {selectedTable?.label}?</>} titleClassName="flex items-center gap-2 text-red-600">
    <Button size="sm" variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleDelete} disabled={deleting} variant="destructive" data-testid="confirm-delete-btn">
                            {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" /> Delete Permanently</>}
                        </Button>
</DialogTopBar>
                    <div className="py-4">
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                            <p className="text-red-700 text-sm font-medium">
                                This will permanently delete <strong>{tableStats[selectedTable?.statKey] || 0}</strong> {selectedTable?.label.toLowerCase()} record{(tableStats[selectedTable?.statKey] || 0) !== 1 ? 's' : ''} and all associated data.
                            </p>
                        </div>
                        <p className="text-gray-500 text-sm">This action cannot be undone.</p>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default DeletionManagementPage;
